import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Link,
  Select,
  Text
} from '@chakra-ui/react';

import Pagination from '../../../components/Pagination';
import { showModal } from '../../../redux/modules/modal';
import { useCompanyImports } from '../../../services/import-service';
import { useMeCompanies } from '../../../services/user-service';
import colors from '../../../styles/theme/foundation/colors';

import CreateImportModal from './CreateImportModal';
import ImportStatusBadge from './ImportStatusBadge';
import { formatDateTime } from './util';

const COMPANY_STORAGE_KEY = 'pinsadmin-imports-company';

const rememberedCompanyId = () => {
  try {
    return Number(window.localStorage.getItem(COMPANY_STORAGE_KEY)) || null;
  } catch (e) {
    return null;
  }
};

const ImportsList = () => {
  const dispatch = useDispatch();
  const [companyId, setCompanyId] = useState(rememberedCompanyId);
  const [pageNumber, setPageNumber] = useState(1);

  const { data: companies = [], isLoading: companiesLoading } = useMeCompanies();
  const { data, isLoading, isFetching, isError, error } = useCompanyImports(companyId, { pageNumber });

  const sortedCompanies = useMemo(
    () => [...companies].sort((a, b) => (a.companyName ?? '').localeCompare(b.companyName ?? '')),
    [companies]
  );
  const company = sortedCompanies.find(entry => entry.companyId === companyId);

  const handleCompanyChange = event => {
    const nextId = Number(event.target.value) || null;

    setCompanyId(nextId);
    setPageNumber(1);

    try {
      window.localStorage.setItem(COMPANY_STORAGE_KEY, nextId ?? '');
    } catch (e) {
      // storage can be unavailable in private windows; the picker still works for the session
    }
  };

  const handleCreate = () => {
    dispatch(showModal(
      CreateImportModal,
      { title: 'New import', size: 'lg' },
      { companyId, companyName: company?.companyName ?? '' }
    ));
  };

  const imports = data?.data ?? [];

  return (
    <Box bg={colors.white} maxH="700px" overflowY="auto" p={6} w="1050px">
      <Text textStyle="displayXs">PINS Admin</Text>

      <Flex alignItems="center" justifyContent="space-between" mt={4}>
        <Text textStyle="bodyLg">Bulk imports of COIs and contracts for a company</Text>

        <Flex alignItems="center" gap={3}>
          <Select
            data-testid="import-company-picker"
            isDisabled={companiesLoading}
            placeholder={companiesLoading ? 'Loading companies…' : 'Pick a company'}
            value={companyId ?? ''}
            w="320px"
            onChange={handleCompanyChange}
          >
            {sortedCompanies.map(entry => (
              <option key={entry.companyId} value={entry.companyId}>{entry.companyName}</option>
            ))}
          </Select>
          <Button isDisabled={!companyId} onClick={handleCreate}>New import</Button>
        </Flex>
      </Flex>

      {!companyId && <Text mt={4}>Pick a company to see its imports.</Text>}

      {companyId && isLoading && <Text mt={4}>Loading...</Text>}

      {isError && (
        <Alert borderRadius="md" mt={4} status="error">
          <AlertIcon />
          <AlertDescription>{error?.message || 'Something went wrong fetching the imports.'}</AlertDescription>
        </Alert>
      )}

      {companyId && !isLoading && !isError && imports.length === 0 && (
        <Text mt={4}>{`No imports yet for ${company?.companyName ?? 'this company'}.`}</Text>
      )}

      {imports.length > 0 && (
        <Box opacity={isFetching ? 0.6 : 1} p={4} textAlign="left">
          <Grid gap={6} gridTemplateColumns="repeat(12, 1fr);">
            <GridItem alignSelf="center" colSpan={1} justifySelf="left" ml={2} textStyle="bodyLg500">#</GridItem>
            <GridItem alignSelf="center" colSpan={4} justifySelf="left" ml={2} textStyle="bodyLg500">Name</GridItem>
            <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2} textStyle="bodyLg500">Source</GridItem>
            <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2} textStyle="bodyLg500">Status</GridItem>
            <GridItem alignSelf="center" colSpan={3} justifySelf="left" ml={2} textStyle="bodyLg500">Created</GridItem>
          </Grid>

          {imports.map(entry => (
            <Grid gap={6} gridTemplateColumns="repeat(12, 1fr);" key={entry.id} my={4}>
              <GridItem alignSelf="center" colSpan={1} justifySelf="left" ml={2}>{entry.id}</GridItem>
              <GridItem alignSelf="center" colSpan={4} justifySelf="left" ml={2}>
                <Link as={RouterLink} color="primaryScale.700" to={`/pinsadmin/imports/${entry.id}`}>{entry.name}</Link>
              </GridItem>
              <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2} textTransform="capitalize">{entry.source}</GridItem>
              <GridItem alignSelf="center" colSpan={2} justifySelf="left" ml={2}>
                <ImportStatusBadge status={entry.status} />
              </GridItem>
              <GridItem alignSelf="center" colSpan={3} justifySelf="left" ml={2}>{formatDateTime(entry.createdAt)}</GridItem>
            </Grid>
          ))}

          {data?.lastPage > 1 && (
            <Pagination
              currentPage={data.currentPage}
              from={data.from}
              handlePageSelection={setPageNumber}
              pageCount={data.lastPage}
              perPage={data.perPage}
              to={data.to}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default ImportsList;
